
import { jitsiLocalStorage } from '@jitsi/js-utils/jitsi-local-storage';
import CircularProgress from '@material-ui/core/CircularProgress';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { v4 as uuidv4 } from 'uuid';

import { closeDialogs } from '../../../base/dialog';
import { Icon, IconBlurBackground, IconCancelSelection, IconUpload } from '../../../base/icons';
import Tooltip from '../../../base/tooltip/components/Tooltip';
import { setCustomBackground, submitVirtualBackground } from '../../actions.web';
import { VIRTUAL_BACKGROUND_TYPE } from '../../constants';
import { getStoredCustomBackgrounds, persistCustomBackgrounds, resizeImage } from '../../functions';
import logger from '../../logger';
import { getVirtualBackgroundOptions } from '../../reducer';

import CustomDialog from './CustomDialog';
import VirtualBackgroundPreview from './VirtualBackgroundPreview';
import { useStyles } from './styles';

// Maximum number of custom uploaded backgrounds allowed
const BACKGROUNDS_LIMIT = 22;

// Built-in background images - PRESERVED from original implementation
const BUILTIN_IMAGES = [
    { id: 1,
        tooltip: 'Bookshelf',
        name: 'bg-bookshelf-meethour-min.jpg',
        src: 'images/virtual-background/bg-bookshelf-meethour-min.jpg' },
    { id: 2,
        tooltip: 'Clock',
        name: 'bg-clock-meethour-min.jpg',
        src: 'images/virtual-background/bg-clock-meethour-min.jpg' },
    { id: 3,
        tooltip: 'Conference Hall',
        name: 'bg-conferencehall-meethour-min.jpg',
        src: 'images/virtual-background/bg-conferencehall-meethour-min.jpg' },
    { id: 4,
        tooltip: 'Cozy Room',
        name: 'bg-cozy-meethour-min.jpg',
        src: 'images/virtual-background/bg-cozy-meethour-min.jpg' },
    { id: 5,
        tooltip: 'Gamer Room',
        name: 'bg-gamer-meethour-min.jpg',
        src: 'images/virtual-background/bg-gamer-meethour-min.jpg' },
    { id: 6,
        tooltip: 'Gamer Room',
        name: 'bg-gamer-room-meethour-min.jpg',
        src: 'images/virtual-background/bg-gamer-room-meethour-min.jpg' },
    { id: 7,
        tooltip: 'Gamer Setup',
        name: 'bg-gamer-room-setup-min.jpg',
        src: 'images/virtual-background/bg-gamer-room-setup-min.jpg' },
    { id: 8,
        tooltip: 'Home',
        name: 'bg-home-meethour-min.jpg',
        src: 'images/virtual-background/bg-home-meethour-min.jpg' },
    { id: 9,
        tooltip: 'Home Kitchen',
        name: 'bg-homie-kitchen-meethour.jpg',
        src: 'images/virtual-background/bg-homie-kitchen-meethour.jpg' },
    { id: 10,
        tooltip: 'Luxury Dinning Area',
        name: 'bg-homie-meethour-min.jpg',
        src: 'images/virtual-background/bg-homie-meethour-min.jpg' },
    { id: 11,
        tooltip: 'Living Room',
        name: 'bg-homie2-meethour.jpg',
        src: 'images/virtual-background/bg-homie2-meethour.jpg' },
    { id: 12,
        tooltip: 'Home Corner',
        name: 'bg-homie3-meethour.jpg',
        src: 'images/virtual-background/bg-homie3-meethour.jpg' },
    { id: 13,
        tooltip: 'Living Space',
        name: 'bg-homie4-meethour.jpg',
        src: 'images/virtual-background/bg-homie4-meethour.jpg' },
    { id: 14,
        tooltip: 'Hotel Room',
        name: 'bg-hotel-room-meethour.jpg',
        src: 'images/virtual-background/bg-hotel-room-meethour.jpg' },
    { id: 15,
        tooltip: 'Lecture Hall',
        name: 'bg-leaturehall-meethour-min.jpg',
        src: 'images/virtual-background/bg-leaturehall-meethour-min.jpg' },
    { id: 16,
        tooltip: 'Lecture Hall',
        name: 'bg-leaturehall2-meethour-min.jpg',
        src: 'images/virtual-background/bg-leaturehall2-meethour-min.jpg' },
    { id: 17,
        tooltip: 'Library',
        name: 'bg-library-setup.jpg',
        src: 'images/virtual-background/bg-library-setup.jpg' },
    { id: 18,
        tooltip: 'Lobby',
        name: 'bg-loob-meethour-min.jpg',
        src: 'images/virtual-background/bg-loob-meethour-min.jpg' },
    { id: 19,
        tooltip: 'Luxury Hotel Room',
        name: 'bg-luxury-hotel-room-meethour.jpg',
        src: 'images/virtual-background/bg-luxury-hotel-room-meethour.jpg' },
    { id: 20,
        tooltip: 'Nature',
        name: 'bg-nature-meethour-min.jpg',
        src: 'images/virtual-background/bg-nature-meethour-min.jpg' },
    { id: 21,
        tooltip: 'Office Conference',
        name: 'bg-office-conference.jpg',
        src: 'images/virtual-background/bg-office-conference.jpg' },
    { id: 22,
        tooltip: 'Office Lobby',
        name: 'bg-office-guest-lobby.jpg',
        src: 'images/virtual-background/bg-office-guest-lobby.jpg' },
    { id: 23,
        tooltip: 'Office Colleagues',
        name: 'bg-office-with-colleagues.jpg',
        src: 'images/virtual-background/bg-office-with-colleagues.jpg' },
    { id: 24,
        tooltip: 'Open Restaurant',
        name: 'bg-open-restro-meethour.jpg',
        src: 'images/virtual-background/bg-open-restro-meethour.jpg' },
    { id: 25,
        tooltip: 'Painting',
        name: 'bg-painting-meethour-min.jpg',
        src: 'images/virtual-background/bg-painting-meethour-min.jpg' },
    { id: 26,
        tooltip: 'Personal Room',
        name: 'bg-personalroom-meethour.jpg',
        src: 'images/virtual-background/bg-personalroom-meethour.jpg' },
    { id: 27,
        tooltip: 'Pottery',
        name: 'bg-pot-meethour-min.jpg',
        src: 'images/virtual-background/bg-pot-meethour-min.jpg' },
    { id: 28,
        tooltip: 'Royal Room',
        name: 'bg-royal-meethour-min.jpg',
        src: 'images/virtual-background/bg-royal-meethour-min.jpg' },
    { id: 29,
        tooltip: 'Ted Talks',
        name: 'bg-tedtalks.jpg',
        src: 'images/virtual-background/bg-tedtalks.jpg' },
    { id: 30,
        tooltip: 'Virtual Background',
        name: 'bg-virtualbg-meethour.jpg',
        src: 'images/virtual-background/bg-virtualbg-meethour.jpg' }
];

/**
 * VirtualBackgroundDialog - A modern, accessible dialog for selecting virtual backgrounds.
 *
 * Features:
 * - Decoupled local UI state from global Redux state
 * - Modern UI with micro-interactions and smooth transitions
 * - WCAG 2.1 compliant accessibility
 * - Optimized performance with memoization
 * - Responsive design for all screen sizes
 *
 * @param {Object} props - Component props.
 * @returns {ReactElement}
 */
function VirtualBackgroundDialog({ selectedVideoInputId }) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { classes: styles, cx } = useStyles();

    // ============ GLOBAL STATE (Redux) - Read Only ============
    const globalOptions = useSelector(getVirtualBackgroundOptions);

    // ============ LOCAL STATE (Dialog UI) ============
    const [ localSelection, setLocalSelection ] = useState(() => {
        return {
            backgroundType: globalOptions.backgroundType || VIRTUAL_BACKGROUND_TYPE.NONE,
            selectedThumbnail: globalOptions.selectedThumbnail || 'none',
            virtualSource: globalOptions.virtualSource || '',
            blurValue: globalOptions.blurValue || 0
        };
    });

    const [ loading, setLoading ] = useState(false);

    // const isMobileView = isMobileScreen();

    const isMobileView = useMediaQuery({ maxWidth: 640 });
    const isBetweenMobileAndTablet = useMediaQuery({ minWidth: 641,
        maxWidth: 900 });

    // const [ mirrorVideo, setMirrorVideo ] = useState(false);
    // const [ greenScreen, setGreenScreen ] = useState(false);

    // Custom uploaded images - stored in localStorage and synced to Redux
    const [ storedImages, setStoredImages ] = useState(getStoredCustomBackgrounds);

    const inputFileRef = useRef(null);

    // Store initial options for cancel functionality
    const initialOptionsRef = useRef({
        backgroundType: globalOptions.backgroundType || VIRTUAL_BACKGROUND_TYPE.NONE,
        selectedThumbnail: globalOptions.selectedThumbnail || 'none',
        virtualSource: globalOptions.virtualSource || '',
        blurValue: globalOptions.blurValue || 0
    });

    // ============ DERIVED STATE ============
    const isCustomImageSelected = useMemo(() => {
        const { selectedThumbnail, backgroundType } = localSelection;

        if (backgroundType !== VIRTUAL_BACKGROUND_TYPE.IMAGE) {
            return false;
        }

        if (!selectedThumbnail || selectedThumbnail === 'none') {
            return false;
        }

        return storedImages.some(img => img && img.id === selectedThumbnail);
    }, [ localSelection, storedImages ]);

    // ============ EFFECTS ============

    useEffect(() => {
        if (storedImages.length > BACKGROUNDS_LIMIT) {
            setStoredImages(prev => prev.slice(prev.length - BACKGROUNDS_LIMIT));

            return;
        }

        // The write evicts the oldest images when they no longer fit in the browser's storage - the previous
        // unguarded setItem threw QuotaExceededError out of this effect instead, after each uploaded image had
        // already filled the origin's storage and started breaking every other feature's persistence.
        const persistedImages = persistCustomBackgrounds(storedImages);

        // Keep the grid in sync with what actually survived, rather than showing thumbnails that vanish on reload.
        if (persistedImages.length !== storedImages.length) {
            setStoredImages(persistedImages);
        }
    }, [ storedImages ]);

    useEffect(() => {
        dispatch(setCustomBackground(storedImages));
    }, [ dispatch, storedImages ]);

    // ============ HANDLERS ============

    const handleSelectNone = useCallback(e => {
        e.preventDefault();
        setLocalSelection({
            backgroundType: VIRTUAL_BACKGROUND_TYPE.NONE,
            selectedThumbnail: 'none',
            virtualSource: '',
            blurValue: 0
        });
        jitsiLocalStorage.removeItem('features/virtual-background/images');
    }, []);

    const handleSelectBlur = useCallback(e => {
        e.preventDefault();
        setLocalSelection({
            backgroundType: VIRTUAL_BACKGROUND_TYPE.BLUR,
            selectedThumbnail: 'blur',
            virtualSource: '',
            blurValue: 25
        });
    }, []);

    const handleSelectBuiltinImage = useCallback(image => {
        setLocalSelection({
            backgroundType: VIRTUAL_BACKGROUND_TYPE.IMAGE,
            selectedThumbnail: image.id,
            virtualSource: image.src,
            blurValue: 0
        });
        jitsiLocalStorage.removeItem('features/virtual-background/images');
    }, []);

    const handleSelectCustomImage = useCallback(image => {
        setLocalSelection({
            backgroundType: VIRTUAL_BACKGROUND_TYPE.IMAGE,
            selectedThumbnail: image.id,
            virtualSource: image.src,
            blurValue: 0
        });
    }, []);

    const handleDeleteCustomImage = useCallback((image, e) => {
        e.stopPropagation();
        if (localSelection.selectedThumbnail === image.id) {
            setLocalSelection({
                backgroundType: VIRTUAL_BACKGROUND_TYPE.NONE,
                selectedThumbnail: 'none',
                virtualSource: '',
                blurValue: 0
            });
        }
        setStoredImages(prev => prev.filter(item => item.id !== image.id));
    }, [ localSelection.selectedThumbnail ]);

    const handleUploadClick = useCallback(e => {
        e.preventDefault();
        inputFileRef.current?.click();
    }, []);

    const handleFileUpload = useCallback(async e => {
        const files = e.target.files;

        if (!files || !files[0]) {
            return;
        }

        setLoading(true);

        const reader = new FileReader();

        reader.readAsDataURL(files[0]);
        reader.onload = async () => {
            try {
                const resizedImage = await resizeImage(reader.result);
                const newImage = {
                    id: uuidv4(),
                    src: resizedImage
                };

                setStoredImages(prev => [ ...prev, newImage ]);
                handleSelectCustomImage(newImage);
                setLoading(false);
            } catch (error) {
                logger.error('Failed to process uploaded image:', error);
                setLoading(false);
            }
        };

        reader.onerror = () => {
            logger.error('Failed to upload virtual image!');
            setLoading(false);
        };

        e.target.value = '';
    }, [ handleSelectCustomImage ]);

    const handleSubmit = useCallback(e => {
        if (e) {
            e.preventDefault();
        }
        setLoading(true);

        if (isCustomImageSelected) {
            const selectedImage = storedImages.find(img => img?.id === localSelection.selectedThumbnail);

            if (selectedImage) {
                jitsiLocalStorage.setItem('features/virtual-background/images', JSON.stringify(selectedImage));
            }
        } else if (localSelection.backgroundType === VIRTUAL_BACKGROUND_TYPE.NONE) {
            jitsiLocalStorage.removeItem('features/virtual-background/images');
        }

        const options = {
            backgroundEffectEnabled: localSelection.backgroundType !== VIRTUAL_BACKGROUND_TYPE.NONE,
            backgroundType: localSelection.backgroundType,
            selectedThumbnail: localSelection.selectedThumbnail,
            virtualSource: localSelection.virtualSource,
            blurValue: localSelection.blurValue
        };

        dispatch(submitVirtualBackground({ options }))
            .then(() => {
                setLoading(false);
                dispatch(closeDialogs());
            })
            .catch(error => {
                logger.error('Failed to submit virtual background:', error);
                setLoading(false);
            });
    }, [ dispatch, localSelection, storedImages, isCustomImageSelected ]);

    const handleCancel = useCallback(() => {
        setLocalSelection(initialOptionsRef.current);
        dispatch(closeDialogs(true));
    }, [ dispatch ]);

    const handleClose = useCallback(() => {
        dispatch(closeDialogs(true));
    }, [ dispatch ]);

    const handlePreviewLoaded = useCallback(loaded => {
        if (loaded) {
            setLoading(false);
        }
    }, []);


    // ============ RENDER ============
    return (
        <CustomDialog
            isOpen = { true }
            onClose = { handleClose }
            titleKey = 'virtualBackground.title'>
            <div className = { styles.dialogModern }>
                {/* Left Section: Preview & Controls */}
                <div className = { styles.leftSection }>
                    {/* Video Preview */}
                    <div className = { styles.previewContainer }>
                        <VirtualBackgroundPreview
                            loadedPreview = { handlePreviewLoaded }
                            localSelection = { localSelection }
                            selectedVideoInputId = { selectedVideoInputId } />
                    </div>

                    {/* Controls Section */}
                    { !(isMobileView || isBetweenMobileAndTablet) && <div className = { styles.controlsSection }>
                        {/* Action Buttons */}
                        <div className = { styles.actionButtons }>
                            <button
                                className = { styles.applyButton }
                                disabled = { loading }
                                onClick = { handleSubmit }
                                type = 'button'>
                                {loading
                                    ? (
                                        <CircularProgress
                                            color = 'inherit'
                                            size = { 16 } />
                                    )
                                    : t('virtualBackground.apply', { defaultValue: 'Save' })}
                            </button>
                            <button
                                className = { styles.cancelButton }
                                onClick = { handleCancel }
                                type = 'button'>
                                {t('virtualBackground.cancel', { defaultValue: 'Cancel' })}
                            </button>
                        </div>

                    </div>}
                </div>

                {/* Right Section: Background Grid */}
                <div className = { styles.rightSection }>
                    <h3 className = { styles.sectionTitle }>
                        {t('virtualBackground.virtualBuiltInImages', { defaultValue: 'Virtual Built-In Images' })}
                    </h3>

                    {/* Quick Action Buttons: None, Blur, Add Custom */}
                    <div className = { styles.quickActionsSection }>
                        <Tooltip
                            content = { t('virtualBackground.removeBackground', { defaultValue: 'Remove background' }) }
                            position = 'top'>
                            <button
                                aria-label = { t('virtualBackground.removeBackground', { defaultValue: 'Remove background' }) }
                                aria-pressed = { localSelection.selectedThumbnail === 'none' }
                                className = { cx(styles.quickActionBtn, {
                                    [styles.quickActionBtnActive]: localSelection.selectedThumbnail === 'none'
                                }) }
                                onClick = { handleSelectNone }
                                type = 'button'>
                                <Icon
                                    size = { 18 }
                                    src = { IconCancelSelection } />
                                <span>{t('virtualBackground.none', { defaultValue: 'None' })}</span>
                            </button>
                        </Tooltip>

                        <Tooltip
                            content = { t('virtualBackground.enableBlur', { defaultValue: 'Enable blur background' }) }
                            position = 'top'>
                            <button
                                aria-label = { t('virtualBackground.enableBlur', { defaultValue: 'Enable blur background' }) }
                                aria-pressed = { localSelection.selectedThumbnail === 'blur' }
                                className = { cx(styles.quickActionBtn, {
                                    [styles.quickActionBtnActive]: localSelection.selectedThumbnail === 'blur'
                                }) }
                                onClick = { handleSelectBlur }
                                type = 'button'>
                                <Icon
                                    size = { 18 }
                                    src = { IconBlurBackground } />
                                <span>{t('virtualBackground.blur', { defaultValue: 'Blur' })}</span>
                            </button>
                        </Tooltip>

                        <button
                            aria-label = 'Upload custom background image'
                            className = { styles.addCustomImageBtn }
                            onClick = { handleUploadClick }
                            type = 'button'>
                            <Icon
                                size = { 20 }
                                src = { IconUpload } />
                            <span className = { styles.addCustomImageText }>
                                {t('virtualBackground.addCustom', { defaultValue: '+ Add Custom' })}
                            </span>
                        </button>
                        <input
                            accept = 'image/*'
                            aria-label = 'Upload custom background image'
                            className = { styles.hiddenInput }
                            onChange = { handleFileUpload }
                            ref = { inputFileRef }
                            type = 'file' />
                    </div>

                    {/* Scrollable Grid */}
                    <div
                        className = { styles.backgroundsGrid }
                        role = 'list'>
                        {/* Custom Uploaded Images - Show first */}
                        {storedImages.map((image, index) => (
                            <div
                                className = { cx(styles.backgroundItem, styles.customImageItemHover, 'vb-new-dialog') }
                                key = { `custom-${image.id}` }
                                role = 'listitem'>
                                <div className = { styles.imageWrapper }>
                                    <img
                                        alt = { `Custom background ${image.id}` }
                                        aria-label = { `Select custom background ${image.id}` }
                                        className = { cx(styles.backgroundImage, {
                                            [styles.imageSelected]: localSelection.selectedThumbnail === image.id
                                        }) }
                                        onClick = { () => handleSelectCustomImage(image) }
                                        onError = { event => {
                                            event.target.style.display = 'none';
                                        } }
                                        onKeyDown = { e => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                handleSelectCustomImage(image);
                                            }
                                        } }
                                        role = 'button'
                                        src = { image.src }
                                        tabIndex = { 0 } />
                                    <button
                                        aria-label = { `Delete custom background ${image.id}` }
                                        className = { cx(styles.deleteBtn, 'vb-new-dialog-child') }
                                        onClick = { e => {
                                            handleDeleteCustomImage(image, e);
                                        } }
                                        type = 'button'>
                                        <Icon
                                            size = { 14 }
                                            src = { IconCancelSelection } />
                                    </button>
                                </div>
                                <div className = { styles.imageLabel }>
                                    Custom {index + 1}
                                </div>
                            </div>
                        ))}

                        {/* Built-in Images */}
                        {BUILTIN_IMAGES.map(image => (
                            <div
                                className = { styles.backgroundItem }
                                key = { image.id }
                                role = 'listitem'>
                                <div className = { styles.imageWrapper }>
                                    <img
                                        alt = { image.tooltip }
                                        aria-label = { `Select ${image.tooltip} background` }
                                        className = { cx(styles.backgroundImage, {
                                            [styles.imageSelected]: localSelection.selectedThumbnail === image.id
                                        }) }
                                        onClick = { () => handleSelectBuiltinImage(image) }
                                        onError = { event => {
                                            event.target.style.display = 'none';
                                        } }
                                        onKeyDown = { e => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                handleSelectBuiltinImage(image);
                                            }
                                        } }
                                        role = 'button'
                                        src = { image.src }
                                        tabIndex = { 0 } />
                                </div>
                                <div className = { styles.imageLabel }>
                                    {image.tooltip}
                                </div>
                            </div>
                        ))}
                    </div>
                    { (isMobileView || isBetweenMobileAndTablet) && <div className = { styles.controlsSection }>
                        {/* Action Buttons */}
                        <div className = { styles.actionButtons }>
                            <button
                                className = { styles.applyButton }
                                disabled = { loading }
                                onClick = { handleSubmit }
                                type = 'button'>
                                {loading
                                    ? (
                                        <CircularProgress
                                            color = 'inherit'
                                            size = { 16 } />
                                    )
                                    : t('virtualBackground.apply', { defaultValue: 'Save' })}
                            </button>
                            <button
                                className = { styles.cancelButton }
                                onClick = { handleCancel }
                                type = 'button'>
                                {t('virtualBackground.cancel', { defaultValue: 'Cancel' })}
                            </button>
                        </div>

                    </div>}
                </div>
            </div>
        </CustomDialog>
    );
}

export default VirtualBackgroundDialog;
